import { GenericMapPoint } from '../modules/topological.editor.map/map.point.generic';
import { m, type MithrilVNode } from '../mithril';
import type { MeasurementPoint } from '../state/measurement.state';

export interface DraggablePointState {
	points: MeasurementPoint[];
	updatePointLocation(id: string, location: google.maps.LatLng): void;
}

export interface DraggablePointOptions {
	state: DraggablePointState;
	point: MeasurementPoint;
	/** Div-pixel position of the point (the caller projects via its own overlay / PointCache). */
	pixel: google.maps.Point;
	keyPrefix: string;
	/** Fires only on a clean click — suppressed when the press turned into a drag. */
	onClick?: (e: Event) => void;
	title?: string;
}

/**
 * Pointer handling for a draggable map vertex on an OverlayView pane. Owns the in-flight drag so a press
 * on the vertex moves the point instead of panning the map, and swallows the trailing click after a drag.
 */
export class MapPointDragger {
	private pointId: string | null = null;
	private state: DraggablePointState | null = null;
	private startMouse: { x: number; y: number } | null = null;
	private startPixel: google.maps.Point | null = null;
	private dragged = false;
	private readonly boundMove = (e: MouseEvent) => this.onMove(e);
	private readonly boundUp = () => this.onUp();

	constructor(private readonly getProjection: () => google.maps.MapCanvasProjection | null) {}

	private begin(state: DraggablePointState, pointId: string, event: MouseEvent) {
		// Claim the press so Google Maps doesn't pan the map out from under the vertex.
		event.stopPropagation();
		event.preventDefault();

		const point = state.points.find((p) => p.id === pointId);
		const projection = this.getProjection();
		if (!point || !projection) {
			return;
		}

		this.state = state;
		this.pointId = pointId;
		this.dragged = false;
		this.startMouse = { x: event.clientX, y: event.clientY };
		this.startPixel = projection.fromLatLngToDivPixel(point.location);

		window.addEventListener('mousemove', this.boundMove);
		window.addEventListener('mouseup', this.boundUp);
	}

	private onMove(event: MouseEvent) {
		if (!this.pointId || !this.state || !this.startMouse || !this.startPixel) {
			return;
		}
		const projection = this.getProjection();
		if (!projection) {
			return;
		}

		this.dragged = true;
		const dx = event.clientX - this.startMouse.x;
		const dy = event.clientY - this.startMouse.y;
		const newPixel = new google.maps.Point(this.startPixel.x + dx, this.startPixel.y + dy);
		const latLng = projection.fromDivPixelToLatLng(newPixel);
		if (latLng) {
			this.state.updatePointLocation(this.pointId, latLng);
		}
	}

	private onUp() {
		this.pointId = null;
		this.state = null;
		this.startMouse = null;
		this.startPixel = null;
		window.removeEventListener('mousemove', this.boundMove);
		window.removeEventListener('mouseup', this.boundUp);
	}

	/** A draggable vertex node centred on `pixel`. `onClick` fires only when the press wasn't a drag. */
	pointNode({ state, point, pixel, keyPrefix, onClick, title }: DraggablePointOptions): MithrilVNode {
		return draggableMapPointNode({
			key: `${keyPrefix}-${point.id}`,
			pixel,
			title,
			onMouseDown: (e) => this.begin(state, point.id, e),
			onClick: (e) => {
				e.stopPropagation();
				if (!this.dragged && onClick) {
					onClick(e);
				}
			},
		});
	}
}

export interface DraggableMapPointView {
	key: string;
	pixel: google.maps.Point;
	title?: string;
	onMouseDown: (e: MouseEvent) => void;
	onClick: (e: Event) => void;
}

export function draggableMapPointNode({
	key,
	pixel,
	title,
	onMouseDown,
	onClick,
}: DraggableMapPointView): MithrilVNode {
	const inner = GenericMapPoint.view({
		key: `${key}-inner`,
		point: new google.maps.Point(0, 0),
		backgroundColor: '#FFFFFF',
		additionalStyles: { cursor: 'grab', position: 'relative' },
		onClick,
	});

	return m(
		'div',
		{
			key,
			style: { position: 'absolute', left: `${pixel.x}px`, top: `${pixel.y}px` },
			title: title ?? 'Drag to move',
			onmousedown: onMouseDown,
		},
		inner
	);
}
